import type { ReactNode } from "react";
import { WorkflowTracker, type AppShellProps, type MilestonesProps } from "@wayflyer/flyui";

import { useSpaNavigate } from "../../hooks/use-spa-navigate";

import { useWorkflowTrackerNavigation } from "./use-workflow-tracker-navigation";
import { useOnboardingWorkflowSteps } from "./use-workflow-steps";
import { useOnboardingWorkflowState } from "./use-workflow-state";
import { buildMilestones, type OnboardingStep, type SubstepStatus } from "./utils";
import { useWorkflowTrackerFooter } from "./use-workflow-tracker-footer";

export interface OnboardingWorkflowTrackerProps {
  customerDomain: string | null;
  countryOfIncorporation?: string;
  currentStepId: string;
  currentSubstepId?: string;
  currentSubstepStatus?: SubstepStatus;
  currentStepStatus?: SubstepStatus;
  hasBusinessCountryData?: boolean;
  hasRevenueData?: boolean;
  hasBusinessLocationData?: boolean;
  hasBusinessDetailsData?: boolean;
  secondaryLogo?: AppShellProps["secondaryLogo"];
  children: ReactNode;
}

export function OnboardingWorkflowTracker({
  customerDomain,
  countryOfIncorporation,
  currentStepId,
  currentSubstepId,
  currentSubstepStatus,
  currentStepStatus,
  hasBusinessCountryData = false,
  hasRevenueData = false,
  hasBusinessLocationData = false,
  hasBusinessDetailsData = false,
  secondaryLogo,
  children,
}: OnboardingWorkflowTrackerProps) {
  const { navigate: navigateCompanyCreation } = useSpaNavigate("CompanyCreation");
  const { navigate: navigateFundingApp } = useSpaNavigate("FundingApp");

  const { completedStepIds, completedSubstepIds, stagesConfig } = useOnboardingWorkflowState({
    customerDomain,
    countryOfIncorporation,
    hasBusinessCountryData,
    hasRevenueData,
    hasBusinessLocationData,
    hasBusinessDetailsData,
  });

  const { handleNavigateToStep, isStateRequired } = useWorkflowTrackerNavigation({
    countryOfIncorporation,
    navigateCompanyCreation,
    navigateFundingApp,
    completedStepIds,
    completedSubstepIds,
    currentStepId,
    currentSubstepId,
  });

  const steps = useOnboardingWorkflowSteps({
    includeBusinessLocation: isStateRequired,
    stagesConfig,
    onNavigateToStep: handleNavigateToStep,
  });

  const milestones = buildMilestones(
    steps,
    completedStepIds,
    completedSubstepIds,
    currentSubstepId,
    currentSubstepStatus,
    currentStepId,
    currentStepStatus
  );
  const stepInfo: MilestonesProps = {
    steps: milestones,
    currentStepId,
    currentSubstepId,
  };

  const footer = useWorkflowTrackerFooter(currentSubstepId);

  return (
    <WorkflowTracker key={currentStepId} stepInfo={stepInfo} secondaryLogo={secondaryLogo} footer={footer}>
      {children}
    </WorkflowTracker>
  );
}

export type { OnboardingStep };
